import React from "react";
import { Link } from "react-router-dom";
import { IoMdNotificationsOutline } from "react-icons/io";
import { MdBuild, MdFactCheck, MdPauseCircleOutline } from "react-icons/md";
import { DropdownMenu } from "components/ui/DropdownMenu";
import { apiGet } from "lib/apiClient";
import { usePlatformStatus } from "contexts/PlatformStatusContext";
import { DEFAULT_MAINTENANCE_MESSAGE, DEFAULT_MARKET_CREATION_PAUSED_MESSAGE } from "components/admin/PlatformNotices";

const POLL_MS = 60000;

/** How many markets are waiting for approval: the one thing in the portal that needs you. */
function usePendingApprovals() {
  const [state, setState] = React.useState({ count: null, loading: true, error: null });
  const load = React.useCallback(() => {
    apiGet("/portal/markets?status=PENDING_APPROVAL&page=1&pageSize=1")
      .then((res) => setState({ count: res.total || 0, loading: false, error: null }))
      .catch((err) => setState((s) => ({ count: s.count, loading: false, error: err.message || "Failed to load notifications" })));
  }, []);
  React.useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);
  return { ...state, reload: load };
}

/**
 * Kuest HeaderNotifications: ghost bell button with a primary unread dot; panel
 * w-85 lg:w-95 max-h-100, header row, divide-y list of p-3 rows with a 42px rounded-md
 * thumbnail, title text-sm/tight semibold, body text-xs/tight muted. Built only from
 * live portal state: platform notices and markets waiting for approval.
 */
export default function HeaderNotifications({ isPartner, triggerClassName }) {
  const { maintenance, marketCreation } = usePlatformStatus();
  const approvals = usePendingApprovals();
  const [open, setOpen] = React.useState(false);
  const [seenSignature, setSeenSignature] = React.useState("");

  const items = [];
  if (maintenance?.active) {
    items.push({
      id: "maintenance",
      icon: <MdBuild className="h-5 w-5 text-amber-500" />,
      title: "Maintenance in progress",
      body: maintenance.message || DEFAULT_MAINTENANCE_MESSAGE,
    });
  }
  if (marketCreation && marketCreation.enabled === false) {
    items.push({
      id: "creation-paused",
      icon: <MdPauseCircleOutline className="h-5 w-5 text-amber-500" />,
      title: "Market creation is paused",
      body: marketCreation.message || DEFAULT_MARKET_CREATION_PAUSED_MESSAGE,
      to: "/portal/markets",
    });
  }
  if (approvals.count > 0) {
    items.push({
      id: "approvals",
      icon: <MdFactCheck className="h-5 w-5 text-primary" />,
      title: isPartner
        ? `${approvals.count.toLocaleString()} of your markets ${approvals.count === 1 ? "is" : "are"} waiting for approval`
        : `${approvals.count.toLocaleString()} ${approvals.count === 1 ? "market is" : "markets are"} waiting for your approval`,
      body: isPartner ? "They open for trading once they are approved." : "Review them so they can open for trading.",
      to: "/portal/approvals",
      time: "Now",
    });
  }

  const signature = items.map((i) => `${i.id}:${i.title}`).join("|");
  const unread = items.length > 0 && signature !== seenSignature;

  function onOpenChange(next) {
    setOpen(next);
    if (next) {
      setSeenSignature(signature);
      approvals.reload();
    }
  }

  const empty = (message, error) => (
    <div className="p-4 text-center text-muted-foreground">
      <IoMdNotificationsOutline className={`mx-auto mb-2 h-8 w-8 opacity-50 ${approvals.loading ? "animate-pulse" : ""}`} aria-hidden="true" />
      <p className={`text-sm ${error ? "text-destructive" : ""}`}>{message}</p>
    </div>
  );

  return (
    <DropdownMenu
      open={open}
      onOpenChange={onOpenChange}
      align="end"
      label="Notifications"
      className="w-[min(21.25rem,calc(100vw-2rem))] !p-0 lg:w-[23.75rem]"
      trigger={({ props }) => (
        <button type="button" {...props} aria-label="Notifications" title="Notifications" className={`relative ${triggerClassName}`}>
          <IoMdNotificationsOutline className="h-[1.35rem] w-[1.35rem]" />
          {unread && <span className="absolute right-1.5 top-0.5 h-3 w-3 rounded-full border-2 border-background bg-primary" aria-hidden="true" />}
        </button>
      )}
    >
      {({ close }) => (
        <>
          <div className="border-b border-border px-3 py-2">
            <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
          </div>
          <div className="max-h-[calc(25rem-2.75rem)] overflow-y-auto overscroll-contain">
            {approvals.loading && items.length === 0
              ? empty("Loading notifications...")
              : items.length === 0 && approvals.error
              ? empty("Failed to load notifications", true)
              : items.length === 0
              ? empty("You have no notifications.")
              : (
                <div className="divide-y divide-border">
                  {items.map((item) => {
                    const body = (
                      <>
                        <span className="flex h-[2.625rem] w-[2.625rem] shrink-0 items-center justify-center rounded-md bg-muted">{item.icon}</span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-start justify-between gap-2">
                            <span className="text-sm font-semibold leading-tight text-foreground">{item.title}</span>
                            {item.time && <span className="shrink-0 text-xs text-muted-foreground">{item.time}</span>}
                          </span>
                          <span className="mt-1 line-clamp-2 block text-xs leading-tight text-muted-foreground">{item.body}</span>
                        </span>
                      </>
                    );
                    return item.to ? (
                      <Link
                        key={item.id}
                        to={item.to}
                        role="menuitem"
                        tabIndex={-1}
                        onClick={() => close(false)}
                        className="flex gap-3 p-3 outline-none transition-colors hover:bg-accent/50 focus:bg-accent/50"
                      >
                        {body}
                      </Link>
                    ) : (
                      <div key={item.id} role="menuitem" tabIndex={-1} className="flex gap-3 p-3 outline-none focus:bg-accent/50">
                        {body}
                      </div>
                    );
                  })}
                </div>
              )}
          </div>
        </>
      )}
    </DropdownMenu>
  );
}
